import React, { useState, useEffect } from 'react';
import { Package, Truck, CheckCircle, Clock, Eye, MapPin, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { buildTrackingUrl } from '../utils/trackingCarrier';
import { formatEUR } from '../utils/format';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/my-orders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-800', icon: Package },
      shipped: { label: 'Versendet', color: 'bg-purple-100 text-purple-800', icon: Truck },
      delivered: { label: 'Zugestellt', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-800', icon: Clock }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const OrderDetailModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Bestellung #{order.order_number}</h2>
                <p className="text-sm text-gray-600">
                  {new Date(order.created_at).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <Button variant="ghost" onClick={onClose}>✕</Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Status */}
            <div>
              <h3 className="font-semibold mb-2">Status</h3>
              {getStatusBadge(order.status)}
              {order.tracking_number && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Truck className="h-4 w-4" />
                    <span>Sendungsnummer: <strong>{order.tracking_number}</strong></span>
                  </div>
                  {(() => {
                    const t = buildTrackingUrl(order.tracking_number);
                    return (
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white text-sm rounded-none transition-colors"
                        data-testid="track-shipment-btn"
                      >
                        {t.icon} Sendung bei {t.carrier} verfolgen
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Products */}
            <div>
              <h3 className="font-semibold mb-3">Bestellte Artikel</h3>
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-gray-600">Menge: {item.quantity}</p>
                    </div>
                    <p className="font-semibold">{formatEUR(((item.price || 0) * (item.quantity || 0)))}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Address */}
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Lieferadresse
              </h3>
              <div className="p-3 bg-gray-50 rounded text-sm">
                <p>{order.shipping_address.name}</p>
                <p>{order.shipping_address.street}</p>
                <p>{order.shipping_address.zip} {order.shipping_address.city}</p>
                {order.shipping_address.country && <p>{order.shipping_address.country}</p>}
              </div>
            </div>

            {/* Price Summary */}
            <div className="border-t pt-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Zwischensumme:</span>
                  <span>{formatEUR((order.subtotal || 0))}</span>
                </div>
                {(order.discount || 0) > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Rabatt {order.coupon_code && `(${order.coupon_code})`}:</span>
                    <span>-{formatEUR((order.discount || 0))}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Versand:</span>
                  <span>{formatEUR((order.shipping_cost || 0))}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Gesamt:</span>
                  <span>{formatEUR((order.total || order.total_amount || 0))}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">Lade Bestellungen...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Meine Bestellungen</h1>
            <p className="text-gray-600">Übersicht über alle Ihre Bestellungen</p>
          </div>

          {/* Orders List */}
          {orders.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">Noch keine Bestellungen</p>
                <p className="text-sm mb-4">Sie haben noch nichts bestellt</p>
                <Button onClick={() => navigate('/products')}>
                  Jetzt shoppen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg">#{order.order_number}</h3>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {new Date(order.created_at).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-sm text-gray-600">
                          {order.items.length} Artikel • Gesamt: <strong>{formatEUR((order.total || order.total_amount || 0))}</strong>
                        </p>
                        {order.tracking_number && (
                          <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-2">
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              Sendung: {order.tracking_number}
                            </span>
                            {(() => {
                              const t = buildTrackingUrl(order.tracking_number);
                              return (
                                <a
                                  href={t.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 text-xs text-[#8B6F4D] hover:text-[#5D4E3C] underline"
                                  data-testid="track-shipment-inline"
                                >
                                  bei {t.carrier} verfolgen
                                  <ExternalLink className="h-2.5 w-2.5" />
                                </a>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => setSelectedOrder(order)}
                        className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Details ansehen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
};

export default MyOrders;
